import React, { useCallback, useEffect, useState } from 'react';
import { SearchBarProps } from './types';
import { useDebounce } from '@/hooks/useDebounce';
import styles from '@/styles/components/models-gallery/SearchBar.module.css';
import SearchInput from '@/components/ui/SearchInput';

const SearchBar: React.FC<SearchBarProps> = ({ 
  value, 
  onChange, 
  placeholder = "Search models...", 
  className 
}) => {
  const [localValue, setLocalValue] = useState(value);

  // Debounce the onChange callback to prevent excessive filtering
  const debouncedOnChange = useDebounce(onChange, 300);

  // Update local value when external value changes
  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  // Handle input change with debouncing
  const handleInputChange = useCallback((newValue: string) => {
    setLocalValue(newValue);
    debouncedOnChange(newValue);
  }, [debouncedOnChange]);

  // Clear search functionality
  const handleClear = useCallback(() => {
    setLocalValue('');
    onChange('');
  }, [onChange]);

  // Handle keyboard events
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      handleClear();
    }
  }, [handleClear]);

  return (
    <div className={`${styles.searchContainer} ${className || ''}`}>
      <SearchInput
        id="models-search"
        value={localValue}
        onChange={handleInputChange}
        placeholder={placeholder}
        ariaLabel="Search models by name or description"
        inputSize="md"
        showClear
        onClear={handleClear}
        onKeyDown={handleKeyDown}
      />
      <div id="search-help" className="sr-only">Search by model name or description. Press Escape to clear.</div>
      <div id="search-instructions" className="sr-only">Type to search models. Results will update automatically as you type.</div>
    </div>
  );
};

export default SearchBar;